import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { useAppFlagStore } from '@frontend/shared';
import { CallIntelSubscriptionPlan } from '../constants';
import { CallIntelSubscriptionPlanValue } from '../types';
import { useCallIntelFeatureAccess } from './use-call-intel-feature-access';
import { useCallIntelSubscription } from './use-call-intel-subscription';
import { useHasCallIntelAclAccess } from './use-has-call-intel-acl-access';

type UseCallIntelColumnsAccessParams = {
  locationIds: string[];
};

type UseCallIntelColumnsAccess = {
  showCallIntelInsights: boolean;
  showTaskColumns: boolean;
  showServiceQuality: boolean;
  isDemoUser: boolean;
  currentPlan: CallIntelSubscriptionPlanValue;
  locationPlanMap: Record<string, CallIntelSubscriptionPlanValue>;
};

export const useCallIntelColumnsAccess = ({
  locationIds,
}: UseCallIntelColumnsAccessParams): UseCallIntelColumnsAccess => {
  const { getFeatureFlagValue } = useAppFlagStore();
  const isServiceQualityEnabled = getFeatureFlagValue({ flagName: 'enable-service-quality', locationIds });

  const {
    hasPermission: hasCallIntelInsightsPermission,
    isFlagEnabled: isCallIntelInsightsFlagEnabled,
    isDemoUser,
  } = useCallIntelFeatureAccess({
    permissions: [Permission.CALL_CLASSIFICATION_READ],
    allowDemoOverride: true,
  });

  const { currentPlan, locationPlanMap } = useCallIntelSubscription({
    locationIds,
  });

  const hasServiceQualityAccess = useHasCallIntelAclAccess([Permission.CALL_INTELLIGENCE_READ_SERVICE_QUALITY]);

  const showCallIntelInsights =
    hasCallIntelInsightsPermission && isCallIntelInsightsFlagEnabled && currentPlan > CallIntelSubscriptionPlan.NONE;

  const showTaskColumns = showCallIntelInsights && currentPlan > CallIntelSubscriptionPlan.BASE;

  const showServiceQuality = showCallIntelInsights && isServiceQualityEnabled && hasServiceQualityAccess;

  return {
    showCallIntelInsights,
    showTaskColumns,
    showServiceQuality,
    isDemoUser,
    currentPlan,
    locationPlanMap,
  };
};
